/*
  choose-set-builder.js
  「選べる5点セット」コレクション風カスタム UI の挙動。MIK-390 / shopify-theme-dev。

  役割:
  - 対象コレクション(インク/ステッカー)を Liquid JSON ビュー(?view=choose5data)で遅延取得し、選択グリッドを構築
  - カテゴリタブ × タトゥースタイルチップ × キーワード検索の AND 合成で絞り込み
  - ライブで「◯/N点」「N点以上で○%OFF」「割引後 ¥◯(目安)」を表示
  - 「まとめてカートに入れる」で選択品を 1 回の /cart/add.js で一括投入 → カートへ

  データ源(重要 / MIK-390 改修2):
  - 従来の /collections/{handle}/products.json はメタフィールドを返さないため、タトゥースタイル
    (custom.tattooStyle)で絞れなかった。そこで templates/collection.choose5data.liquid の
    JSON ビューへ差し替え、基底データ + メタフィールドを 1 ソースで取得する。
  - 価格は cents 整数(Liquid の variant.price)で受け取る。products.json 時代の "1500.00" 文字列
    パース(toCents)は不要になったため撤去済み。

  重要:
  - Revy は一切駆動しない。割引の成立は Shopify 標準の自動割引(Amount off products)が
    カート/checkout でサーバー側判定する。ここでの割引後価格は UI 側の「目安」表示のみ。
  - 数百点でも CWV を守るため:
      * 商品は初期 HTML に入れず products.json で遅延取得(初期ページ重量を軽く保つ)
      * カードは DocumentFragment で 1 回だけ挿入(reflow 最小化)
      * カードは content-visibility:auto(CSS 側)で画面外の描画コストを遅延
      * 画像は loading=lazy + width 指定で CLS/転送量を抑制
  - INP を守るためイベントはルート要素に委譲(delegation)。
  - 割引率・最小点数・money_format・対象コレクションは data 属性/JSON でセクションから受け取る。
*/
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // Shopify money_format の代表的トークンに対応した簡易フォーマッタ。cents = 通貨サブユニット整数。
  function formatMoney(cents, format) {
    var num = Number(cents) || 0;
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withCommas(n) { return n.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,'); }
    function decimal(n, precision) {
      var amount = (Math.abs(n) / 100).toFixed(precision);
      var parts = amount.split('.');
      return withCommas(parts[0]) + (parts[1] ? '.' + parts[1] : '');
    }

    var fmt = format || '¥{{amount_no_decimals}}';
    var token = (fmt.match(placeholderRegex) || [])[1];
    var value;
    switch (token) {
      case 'amount':
      case 'amount_with_comma_separator':
        value = decimal(num, 2); break;
      case 'amount_no_decimals':
      case 'amount_no_decimals_with_comma_separator':
      default:
        value = decimal(num, 0);
    }
    return fmt.replace(placeholderRegex, value);
  }

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function init(root) {
    if (root.__csInited) return;
    root.__csInited = true;

    var minQty = parseInt(root.dataset.minQty, 10) || 5;
    var discountPercent = parseFloat(root.dataset.discountPercent) || 0;
    var moneyFormat = root.dataset.moneyFormat || '¥{{amount_no_decimals}}';
    var cartAddUrl = (window.routes && window.routes.cart_add_url) || root.dataset.cartAddUrl || '/cart/add.js';
    var cartUrl = (window.routes && window.routes.cart_url) || root.dataset.cartUrl || '/cart';

    var grid = root.querySelector('[data-cs-grid]');
    var emptyEl = root.querySelector('[data-cs-empty]');
    var loadingEl = root.querySelector('[data-cs-loading]');
    var statusEl = root.querySelector('[data-cs-status]');
    var priceEl = root.querySelector('[data-cs-price]');
    var submitEl = root.querySelector('[data-cs-submit]');
    var searchInput = root.querySelector('[data-cs-search]');
    var tabsWrap = root.querySelector('[data-cs-tabs]');
    var stylesWrap = root.querySelector('[data-cs-styles]');
    var stylesRow = root.querySelector('[data-cs-styles-row]');
    // タトゥースタイルチップの表示可否(セクション設定)。'false' 明示時のみ無効。
    var styleFilterEnabled = root.dataset.csStyleEnabled !== 'false';

    var collections = [];
    try {
      var cfgEl = root.querySelector('[data-cs-collections]');
      collections = cfgEl ? JSON.parse(cfgEl.textContent || '[]') : [];
    } catch (e) { collections = []; }

    var activeType = 'all';
    var activeStyle = 'all';
    var searchTerm = '';
    var cards = [];

    // ---- 商品取得(コレクション毎・ページング) ----
    // Liquid JSON ビュー(templates/collection.choose5data.liquid)を叩く。
    // products.json と違いメタフィールド(tattooStyle)を含む。本文は text/html だが中身は JSON。
    function fetchCollection(col) {
      var out = [];
      function page(p) {
        var url = '/collections/' + encodeURIComponent(col.handle) + '?view=choose5data&page=' + p;
        return fetch(url, { headers: { Accept: 'text/html' } })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (text) {
            var data;
            try { data = JSON.parse(text); } catch (e) { data = { products: [], has_next: false }; }
            var products = (data && data.products) || [];
            products.forEach(function (pr) { pr.__type = col.label; });
            out = out.concat(products);
            if (data && data.has_next && p < 20) return page(p + 1); // 安全上限
            return out;
          })
          .catch(function () { return out; });
      }
      return page(1);
    }

    function buildCard(product) {
      var variants = (product.variants || []).filter(function (v) { return v; });
      var available = variants.some(function (v) { return v.available; });
      // JSON ビューは featured_image を width=400 済みの URL 文字列で返す。
      var img = product.featured_image || '';

      var card = document.createElement('div');
      card.className = 'cs-card';
      card.setAttribute('role', 'listitem');
      card.setAttribute('data-cs-card', '');
      card.setAttribute('data-type', product.__type || '');
      card.setAttribute('data-title', product.title || '');
      card.setAttribute('data-available', String(available));
      // タトゥースタイル配列は DOM 属性ではなく JS プロパティで保持(フィルタ毎の再パースを避け INP を守る)。
      card.__styles = Array.isArray(product.tattooStyle) ? product.tattooStyle : [];

      var media =
        '<div class="cs-card__media">' +
        (img
          ? '<img class="cs-card__img" src="' + escapeHtml(img) +
            '" alt="' + escapeHtml(product.title) + '" loading="lazy" decoding="async" width="400" height="400">'
          : '') +
        '</div>';

      var priceCents = variants.length ? Number(variants[0].price) || 0 : 0;

      var variantControl = '';
      if (variants.length > 1) {
        var opts = variants.map(function (v) {
          return '<option value="' + v.id + '" data-price="' + (Number(v.price) || 0) +
            '" data-available="' + (v.available ? 'true' : 'false') + '"' +
            (v.available ? '' : ' disabled') + '>' +
            escapeHtml(v.title) + (v.available ? '' : '(在庫なし)') + '</option>';
        }).join('');
        variantControl =
          '<label class="visually-hidden" for="cs-var-' + product.id + '">バリエーションを選択</label>' +
          '<select class="cs-card__variant" id="cs-var-' + product.id + '" data-cs-variant>' + opts + '</select>';
        var firstAvail = variants.filter(function (v) { return v.available; })[0];
        if (firstAvail) priceCents = Number(firstAvail.price) || 0;
      } else if (variants.length === 1) {
        card.setAttribute('data-variant-id', variants[0].id);
        card.setAttribute('data-price', Number(variants[0].price) || 0);
      }

      var control = available
        ? variantControl +
          '<div class="cs-qty" role="group" aria-label="' + escapeHtml(product.title) + ' の数量">' +
          '<button type="button" class="cs-qty__btn" data-cs-step="minus" aria-label="数量を減らす">−</button>' +
          '<input class="cs-qty__input" type="number" inputmode="numeric" min="0" step="1" value="0" ' +
          'aria-label="' + escapeHtml(product.title) + ' の数量" data-cs-qty-input>' +
          '<button type="button" class="cs-qty__btn" data-cs-step="plus" aria-label="数量を増やす">＋</button>' +
          '</div>'
        : '<p class="cs-card__soldout">在庫なし</p>';

      card.innerHTML =
        media +
        '<div class="cs-card__body">' +
        '<h3 class="cs-card__title">' + escapeHtml(product.title) + '</h3>' +
        '<p class="cs-card__price">' + formatMoney(priceCents, moneyFormat) + '</p>' +
        control +
        '</div>';

      return card;
    }

    function cardVariant(card) {
      var select = card.querySelector('[data-cs-variant]');
      if (select) {
        var opt = select.options[select.selectedIndex];
        return {
          id: opt ? opt.value : null,
          price: opt ? parseInt(opt.getAttribute('data-price'), 10) || 0 : 0,
          available: opt ? opt.getAttribute('data-available') === 'true' : false
        };
      }
      return {
        id: card.getAttribute('data-variant-id') || null,
        price: parseInt(card.getAttribute('data-price'), 10) || 0,
        available: card.getAttribute('data-available') === 'true'
      };
    }

    function cardQtyInput(card) { return card.querySelector('[data-cs-qty-input]'); }

    // 選択バリエーション変更で表示価格を更新
    function updateCardPrice(card) {
      var v = cardVariant(card);
      var el = card.querySelector('.cs-card__price');
      if (el) el.innerHTML = formatMoney(v.price, moneyFormat);
    }

    function recompute() {
      var totalQty = 0;
      var subtotal = 0;
      cards.forEach(function (card) {
        var input = cardQtyInput(card);
        if (!input) return;
        var q = parseInt(input.value, 10);
        if (isNaN(q) || q < 0) q = 0;
        var v = cardVariant(card);
        totalQty += q;
        subtotal += q * v.price;
        card.classList.toggle('cs-selected', q > 0);
      });

      var eligible = totalQty >= minQty;
      var remaining = Math.max(0, minQty - totalQty);

      if (statusEl) {
        var countHtml =
          '<span class="cs-summary__count' + (eligible ? ' is-ok' : '') + '">' +
          totalQty + ' / ' + minQty + '点</span>';
        var msg;
        if (totalQty === 0) msg = minQty + '点以上で' + discountPercent + '%OFF';
        else if (eligible) msg = discountPercent + '%OFF 適用対象です';
        else msg = 'あと' + remaining + '点で' + discountPercent + '%OFF';
        statusEl.innerHTML = countHtml + ' <span>' + msg + '</span>';
      }

      if (priceEl) {
        if (totalQty === 0) {
          priceEl.innerHTML = '';
        } else if (eligible && discountPercent > 0) {
          // 景表法(有利誤認)配慮: 目安が実額より安く出ないよう切り上げ(Math.ceil)。
          // Shopify 側の端数処理と厳密一致はできないため、安全側=お得さを控えめに見せる。
          var discounted = Math.ceil(subtotal * (100 - discountPercent) / 100);
          priceEl.innerHTML =
            '<del>' + formatMoney(subtotal, moneyFormat) + '</del>' +
            '<strong>' + formatMoney(discounted, moneyFormat) + '</strong>' +
            '<span class="cs-summary__note">表示は割引適用後の目安価格です。端数処理により、実際のお支払い金額はカートでの確定額と異なる場合があります。</span>';
        } else {
          priceEl.innerHTML =
            '<strong>' + formatMoney(subtotal, moneyFormat) + '</strong>' +
            '<span class="cs-summary__note">' + minQty + '点以上で' + discountPercent + '%OFF(カートで適用)</span>';
        }
      }

      if (submitEl) submitEl.disabled = totalQty === 0;
    }

    function applyFilter() {
      var term = searchTerm.trim().toLowerCase();
      var visible = 0;
      cards.forEach(function (card) {
        var type = card.getAttribute('data-type') || '';
        var title = (card.getAttribute('data-title') || '').toLowerCase();
        var matchType = activeType === 'all' || type === activeType;
        var matchTerm = term === '' || title.indexOf(term) !== -1;
        // タトゥースタイルは list 型(1商品に複数値)。選択値を配列が含めば一致。
        var matchStyle = activeStyle === 'all' ||
          (card.__styles && card.__styles.indexOf(activeStyle) !== -1);
        var show = matchType && matchTerm && matchStyle;
        card.classList.toggle('cs-hidden', !show);
        if (show) visible++;
      });
      if (emptyEl) emptyEl.hidden = visible !== 0;
      if (grid) grid.hidden = visible === 0;
    }

    function buildTabs(labels) {
      if (!tabsWrap || labels.length <= 1) return;
      var html = '<button type="button" class="cs-tab" data-cs-tab="all" aria-pressed="true">すべて</button>';
      labels.forEach(function (l) {
        html += '<button type="button" class="cs-tab" data-cs-tab="' + escapeHtml(l) +
          '" aria-pressed="false">' + escapeHtml(l) + '</button>';
      });
      tabsWrap.innerHTML = html;
      tabsWrap.hidden = false;
    }

    // 読み込んだ商品が実際に持つ tattooStyle 値からチップを動的生成(存在しない値のチップは出さない)。
    function buildStyleChips(styles) {
      if (!styleFilterEnabled || !stylesWrap || !stylesRow || !styles.length) return;
      var html = '<button type="button" class="cs-style-chip" data-cs-style="all" aria-pressed="true">すべて</button>';
      styles.forEach(function (s) {
        html += '<button type="button" class="cs-style-chip" data-cs-style="' + escapeHtml(s) +
          '" aria-pressed="false">' + escapeHtml(s) + '</button>';
      });
      stylesWrap.innerHTML = html;
      stylesRow.hidden = false;
    }

    // ---- イベント委譲(INP 配慮) ----
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cs-step]');
      if (btn) {
        var card = btn.closest('[data-cs-card]');
        if (!card) return;
        var input = cardQtyInput(card);
        if (!input) return;
        var dir = btn.getAttribute('data-cs-step') === 'plus' ? 1 : -1;
        var cur = parseInt(input.value, 10);
        if (isNaN(cur)) cur = 0;
        input.value = Math.max(0, cur + dir);
        recompute();
        return;
      }
      var tab = e.target.closest('[data-cs-tab]');
      if (tab && tabsWrap) {
        activeType = tab.getAttribute('data-cs-tab');
        Array.prototype.forEach.call(tabsWrap.querySelectorAll('[data-cs-tab]'), function (t) {
          t.setAttribute('aria-pressed', String(t === tab));
        });
        applyFilter();
        return;
      }
      var chip = e.target.closest('[data-cs-style]');
      if (chip && stylesWrap) {
        activeStyle = chip.getAttribute('data-cs-style');
        Array.prototype.forEach.call(stylesWrap.querySelectorAll('[data-cs-style]'), function (c) {
          c.setAttribute('aria-pressed', String(c === chip));
        });
        applyFilter();
      }
    });

    root.addEventListener('input', function (e) {
      if (e.target.matches('[data-cs-qty-input]')) {
        var v = parseInt(e.target.value, 10);
        if (isNaN(v) || v < 0) e.target.value = 0;
        recompute();
      }
    });

    root.addEventListener('change', function (e) {
      if (e.target.matches('[data-cs-variant]')) {
        var card = e.target.closest('[data-cs-card]');
        if (card) updateCardPrice(card);
        recompute();
      }
    });

    if (searchInput) {
      searchInput.addEventListener('input', debounce(function () {
        searchTerm = searchInput.value || '';
        applyFilter();
      }, 200));
    }

    // ---- カート投入 ----
    function setBusy(busy) {
      if (!submitEl) return;
      submitEl.setAttribute('aria-busy', String(busy));
      submitEl.disabled = busy;
    }

    if (submitEl) {
      submitEl.addEventListener('click', function () {
        var items = [];
        cards.forEach(function (card) {
          var input = cardQtyInput(card);
          if (!input) return;
          var q = parseInt(input.value, 10);
          var v = cardVariant(card);
          if (q > 0 && v.id && v.available) items.push({ id: Number(v.id), quantity: q });
        });
        if (items.length === 0) return;

        setBusy(true);
        fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) throw data;
              return data;
            });
          })
          .then(function () {
            // 割引の実額はカートで Shopify 標準割引が確定させるため /cart へ遷移して見せる。
            window.location.href = cartUrl;
          })
          .catch(function (err) {
            setBusy(false);
            var message = (err && err.description) || (err && err.message) ||
              'カートへの追加に失敗しました。時間をおいて再度お試しください。';
            if (statusEl) statusEl.innerHTML = '<span role="alert">' + escapeHtml(message) + '</span>';
            else window.alert(message);
          });
      });
    }

    // ---- 起動: 商品取得 → 構築 ----
    if (!collections.length) {
      if (loadingEl) loadingEl.hidden = true;
      if (emptyEl) { emptyEl.hidden = false; emptyEl.textContent = '対象コレクションが設定されていません。'; }
      return;
    }

    Promise.all(collections.map(fetchCollection)).then(function (results) {
      var seen = {};
      var labels = [];
      var styles = [];
      var styleSeen = {};
      var frag = document.createDocumentFragment();

      results.forEach(function (products) {
        products.forEach(function (product) {
          if (seen[product.id]) return; // コレクション間の重複を排除
          seen[product.id] = true;
          if (labels.indexOf(product.__type) === -1 && product.__type) labels.push(product.__type);
          // タトゥースタイル値を出現順にユニーク収集(存在する値だけがチップになる)
          if (Array.isArray(product.tattooStyle)) {
            product.tattooStyle.forEach(function (s) {
              if (s && !styleSeen[s]) { styleSeen[s] = true; styles.push(s); }
            });
          }
          var card = buildCard(product);
          cards.push(card);
          frag.appendChild(card);
        });
      });

      if (loadingEl) loadingEl.hidden = true;

      if (!cards.length) {
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      buildTabs(labels);
      buildStyleChips(styles);
      grid.appendChild(frag); // reflow 1 回
      recompute();
      applyFilter();
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-cs-builder]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-cs-builder]') : null;
    if (root) { root.__csInited = false; init(root); }
  });
})();
